<script setup>
import { RouterLink } from 'vue-router';
import EditableContent from '../components/EditableContent.vue';
</script>

<template>
  <div class="space-y-12">
    <!-- Hero Section -->
    <EditableContent
      content-id="hero-section"
      view-name="home"
      content-type="html"
    >
      <div class="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl overflow-hidden" data-content-id="hero-section">
        <div class="absolute inset-0 bg-black opacity-10"></div>
        <div class="relative px-8 py-16 sm:px-12 sm:py-24 text-center text-white">
          <h1 class="text-5xl sm:text-6xl font-bold mb-6 leading-tight">
            Manuel Rodríguez de Viguri
          </h1>
          <p class="text-xl sm:text-2xl text-indigo-100 mb-8 max-w-3xl mx-auto">
            Software Engineer • Electronic Music Creator • Cultural Enthusiast
          </p>
          <p class="text-lg text-indigo-50 mb-10 max-w-2xl mx-auto leading-relaxed">
            Bridging legacy systems and modern technology. Exploring the intersection of code, music, and culture.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <RouterLink
              to="/about"
              class="px-8 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition-colors"
            >
              Learn More About Me
            </RouterLink>
            <RouterLink
              to="/professional"
              class="px-8 py-3 bg-indigo-700 text-white rounded-lg font-semibold hover:bg-indigo-800 transition-colors border border-indigo-500"
            >
              View My Work
            </RouterLink>
          </div>
        </div>
      </div>
    </EditableContent>

    <!-- Three Pillars Preview -->
    <div class="py-8">
      <EditableContent
        content-id="pillars-intro"
        view-name="home"
        content-type="text"
      >
        <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center" data-content-id="pillars-intro">
          Three Dimensions of My Work
        </h2>
      </EditableContent>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- Software Development Card -->
        <EditableContent
          content-id="card1"
          view-name="home"
          content-type="html"
        >
          <div class="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow" data-content-id="card1">
            <div class="flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-lg mb-4">
              <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4m0 6V4m0 0L8 4m6 16v-2a9 9 0 00-9-9" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Professional</h3>
            <p class="text-gray-600 mb-4">
              Over a decade specializing in mainframe modernization, Java backend development, and system integration at UBS.
            </p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-sm">Java</span>
              <span class="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-sm">Kafka</span>
              <span class="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-sm">Cloud</span>
            </div>
          </div>
        </EditableContent>

        <!-- Creative Card -->
        <EditableContent
          content-id="card2"
          view-name="home"
          content-type="html"
        >
          <div class="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow" data-content-id="card2">
            <div class="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Creative</h3>
            <p class="text-gray-600 mb-4">
              Electronic music producer and DJ exploring deep house, techno, and progressive beats. Creating immersive sonic experiences.
            </p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 bg-purple-50 text-purple-700 rounded text-sm">Deep House</span>
              <span class="px-2 py-1 bg-purple-50 text-purple-700 rounded text-sm">Techno</span>
              <span class="px-2 py-1 bg-purple-50 text-purple-700 rounded text-sm">DJ</span>
            </div>
          </div>
        </EditableContent>

        <!-- Cultural Card -->
        <EditableContent
          content-id="card3"
          view-name="home"
          content-type="html"
        >
          <div class="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow" data-content-id="card3">
            <div class="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Cultural</h3>
            <p class="text-gray-600 mb-4">
              President of Männerchor Herrliberg and passionate astronomer. Bridging traditional culture with modern exploration.
            </p>
            <div class="flex flex-wrap gap-2">
              <span class="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">Choir</span>
              <span class="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">Astronomy</span>
              <span class="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">Community</span>
            </div>
          </div>
        </EditableContent>
      </div>
    </div>

    <!-- Featured Projects Preview -->
    <div class="py-8">
      <EditableContent
        content-id="featured-intro"
        view-name="home"
        content-type="text"
      >
        <h2 class="text-3xl font-bold text-gray-900 mb-8 text-center" data-content-id="featured-intro">
          Featured Projects
        </h2>
      </EditableContent>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <EditableContent
          content-id="featured-project-1"
          view-name="home"
          content-type="html"
        >
          <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-content-id="featured-project-1">
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Quantum Computing Simulator</h3>
            <p class="text-gray-600 mb-4">
              Interactive quantum computing visualization and learning platform built with Python and Streamlit.
            </p>
            <RouterLink to="/projects" class="text-indigo-600 hover:text-indigo-700 font-medium">
              Explore Projects →
            </RouterLink>
          </div>
        </EditableContent>

        <EditableContent
          content-id="featured-project-2"
          view-name="home"
          content-type="html"
        >
          <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-content-id="featured-project-2">
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Technical Insights Blog</h3>
            <p class="text-gray-600 mb-4">
              Articles on quantum computing, AI, system integration, and project management for tech professionals.
            </p>
            <RouterLink to="/blog" class="text-indigo-600 hover:text-indigo-700 font-medium">
              Read Articles →
            </RouterLink>
          </div>
        </EditableContent>
      </div>
    </div>
  </div>
</template>
